/* ===========================================================
   Lucraa — utilitários PUROS de intervalo de datas
   Base do componente reutilizável DateRangePicker. Sem React,
   sem imports de dados. Datas são ISO "yyyy-mm-dd" (sem hora):
   a comparação lexicográfica de strings já é cronológica, e a
   aritmética usa UTC para não escorregar um dia em fusos/DST.
   Formatação para o usuário é da tela (domain/format.ts).
   =========================================================== */

/** Intervalo inclusivo: `start` e `end` em ISO "yyyy-mm-dd". */
export interface DateRange {
  start: string;
  end: string;
}

/** Célula do grid mensal: a data ISO e se pertence ao mês exibido. */
export interface DayCell {
  iso: string;
  inMonth: boolean;
}

const DIA_MS = 86_400_000;

function partes(iso: string): [number, number, number] {
  // iso é sempre "yyyy-mm-dd" por contrato; Number(undefined) → NaN cobre o
  // caso impossível sem mudar o resultado para entradas válidas.
  const [y, m, d] = iso.split("-");
  return [Number(y), Number(m), Number(d)];
}

const pad2 = (n: number): string => String(n).padStart(2, "0");

/** Date (horário local) → ISO "yyyy-mm-dd". */
export function toISO(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Soma `n` dias (pode ser negativo) a uma data ISO, via UTC. */
export function addDays(iso: string, n: number): string {
  const [y, m, d] = partes(iso);
  const t = new Date(Date.UTC(y, m - 1, d) + n * DIA_MS);
  return `${t.getUTCFullYear()}-${pad2(t.getUTCMonth() + 1)}-${pad2(t.getUTCDate())}`;
}

/** Quantidade de dias do intervalo, contando as duas pontas. */
export function daysInRange(r: DateRange): number {
  const [y1, m1, d1] = partes(r.start);
  const [y2, m2, d2] = partes(r.end);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / DIA_MS) + 1;
}

/** Garante start ≤ end ordenando duas datas ISO. */
export function orderRange(a: string, b: string): DateRange {
  return a <= b ? { start: a, end: b } : { start: b, end: a };
}

/** `inner` está totalmente contido em `outer`? (pontas inclusivas) */
export function rangeContains(outer: DateRange, inner: DateRange): boolean {
  return inner.start >= outer.start && inner.end <= outer.end;
}

/** Janela inclusiva dos últimos `n` dias terminando em `todayISO`. */
export function lastNDays(todayISO: string, n: number): DateRange {
  return { start: addDays(todayISO, -(n - 1)), end: todayISO };
}

/** Um único dia como intervalo. */
export function dayRange(iso: string): DateRange {
  return { start: iso, end: iso };
}

/** Mês corrente até hoje (MTD): 1º do mês de `todayISO` → `todayISO`. */
export function monthToDate(todayISO: string): DateRange {
  return { start: `${todayISO.slice(0, 7)}-01`, end: todayISO };
}

/** Mês anterior completo (1º ao último dia) relativo a `todayISO`. */
export function lastMonthRange(todayISO: string): DateRange {
  const fim = addDays(`${todayISO.slice(0, 7)}-01`, -1); // último dia do mês passado
  return { start: `${fim.slice(0, 7)}-01`, end: fim };
}

/** Navega `delta` meses a partir de (year, month1) — month1 é 1–12. */
export function shiftMonth(
  year: number,
  month1: number,
  delta: number,
): { year: number; month1: number } {
  const idx = year * 12 + (month1 - 1) + delta;
  return { year: Math.floor(idx / 12), month1: (idx % 12) + 1 };
}

/** Grid de 6 semanas (42 células, domingo→sábado) cobrindo o mês.
 *  Células fora do mês vêm com `inMonth: false` para esmaecer na UI. */
export function monthGrid(year: number, month1: number): DayCell[] {
  const primeiroDiaSemana = new Date(Date.UTC(year, month1 - 1, 1)).getUTCDay(); // 0 = dom
  const inicio = addDays(`${year}-${pad2(month1)}-01`, -primeiroDiaSemana);
  const cells: DayCell[] = [];
  for (let i = 0; i < 42; i++) {
    const iso = addDays(inicio, i);
    cells.push({ iso, inMonth: Number(iso.slice(5, 7)) === month1 });
  }
  return cells;
}
